import { useState } from 'react';
import { Project } from '@vault/shared';
import { Plus, Folder, MoreVertical, Edit2, Trash2, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import CreateProjectModal from './CreateProjectModal';
import ConfirmDialog from './ConfirmDialog';

interface SidebarProps {
  projects: Project[];
  selectedProject: Project | null;
  onSelectProject: (project: Project) => void;
  onCreateProject: (name: string) => Promise<Project>;
  onUpdateProject: (id: number, name: string) => Promise<Project>;
  onDeleteProject: (id: number) => Promise<void>;
  loading: boolean;
}

export default function Sidebar({
  projects,
  selectedProject,
  onSelectProject,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
  loading,
}: SidebarProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);

  const handleCreateProject = async (name: string) => {
    const newProject = await onCreateProject(name);
    setShowCreateModal(false);
    onSelectProject(newProject);
  };

  const handleUpdateProject = async (name: string) => {
    if (!editingProject) return;
    await onUpdateProject(editingProject.id, name);
    setEditingProject(null);
  };

  const handleDeleteProject = async () => {
    if (!deletingProject) return;
    await onDeleteProject(deletingProject.id);
    setDeletingProject(null);
    setActiveMenu(null);
    if (selectedProject?.id === deletingProject.id) {
      onSelectProject(projects.find(p => p.id !== deletingProject.id) || projects[0] || null);
    }
  };

  return (
    <>
      <div className="card h-full flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Projects</h2>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary p-2"
              title="Create new project"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Projects List */}
        <div className="flex-1 overflow-y-auto">
          {loading && projects.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : projects.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <Folder className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No projects yet</p>
              <p className="text-xs text-gray-400 mt-1">Create your first project to get started</p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className={clsx(
                    'group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors relative',
                    selectedProject?.id === project.id
                      ? 'bg-primary-50 border border-primary-200'
                      : 'hover:bg-gray-50'
                  )}
                  onClick={() => onSelectProject(project)}
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <Folder 
                      className={clsx(
                        'w-4 h-4 flex-shrink-0',
                        selectedProject?.id === project.id ? 'text-primary-600' : 'text-gray-400'
                      )} 
                    />
                    <div className="min-w-0 flex-1">
                      <p className={clsx(
                        'text-sm font-medium truncate',
                        selectedProject?.id === project.id ? 'text-primary-900' : 'text-gray-900'
                      )}>
                        {project.name}
                      </p>
                      {project.envVars !== undefined && (
                        <p className="text-xs text-gray-500">
                          {project.envVars.length} variables
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenu(activeMenu === project.id ? null : project.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-opacity"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-400" />
                    </button>

                    {activeMenu === project.id && (
                      <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingProject(project);
                            setActiveMenu(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Edit2 className="w-3 h-3 mr-2" />
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingProject(project);
                            setActiveMenu(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3 mr-2" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close menu */}
      {activeMenu && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setActiveMenu(null)}
        />
      )}

      {/* Modals */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateProject}
        title="Create Project"
      />

      <CreateProjectModal
        isOpen={!!editingProject}
        onClose={() => setEditingProject(null)}
        onSubmit={handleUpdateProject}
        title="Edit Project"
        initialValue={editingProject?.name}
      />

      <ConfirmDialog
        isOpen={!!deletingProject}
        onClose={() => setDeletingProject(null)}
        onConfirm={handleDeleteProject}
        title="Delete Project"
        message={`Are you sure you want to delete "${deletingProject?.name}"? This will also delete all environment variables associated with this project.`}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </>
  );
}